import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AlertService } from '../../../../services/alert.service';
import {
  UsersService,
  UserItem,
  CreateUserPayload,
  UpdateUserPayload
} from '../../../../services/users.service';
import AnimatedModal from '../../../../components/modals/animated-modal.component';
import Input from '../../../../components/forms/input.component';
import Button from '../../../../components/forms/button.component';
import Icon from '../../../../components/forms/icon.component';

declare const KTComponents: any;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  editing?: UserItem | null;
  /** Un admin no puede asignar el rol superadmin. */
  canAssignSuperAdmin?: boolean;
}

interface FormValues {
  name: string;
  email: string;
  username: string;
  password: string;
  role: string;
  enabled: boolean;
  forcePasswordChange: boolean;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Valores de rol que espera el backend (enum en español): operador | supervisor | admin | superadmin
const ROLE_VALUES = {
  OPERADOR: 'operador',
  SUPERVISOR: 'supervisor',
  ADMIN: 'admin',
  SUPER_ADMIN: 'superadmin'
} as const;

const defaults: FormValues = {
  name: '',
  email: '',
  username: '',
  password: '',
  role: ROLE_VALUES.OPERADOR,
  enabled: true,
  forcePasswordChange: true
};

function UserFormModal({ isOpen, onClose, onSaved, editing, canAssignSuperAdmin = false }: Props) {
  const { t } = useTranslation();
  const isEdit = Boolean(editing);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting }
  } = useForm<FormValues>({ mode: 'onChange', defaultValues: defaults });

  useEffect(() => {
    if (!isOpen) return;
    if (editing) {
      reset({
        name: editing.name || '',
        email: editing.email || '',
        username: editing.username || '',
        password: '',
        role: editing.roles?.[0] || ROLE_VALUES.OPERADOR,
        enabled: editing.enabled ?? true,
        forcePasswordChange: editing.forcePasswordChange ?? true
      });
    } else {
      reset(defaults);
    }
  }, [isOpen, editing, reset]);

  // Inicializa el toggle de visibilidad de contraseña (KTPasswordMeter de Metronic).
  useEffect(() => {
    if (isOpen && !isEdit) {
      setTimeout(() => KTComponents?.init?.(), 0);
    }
  }, [isOpen, isEdit]);

  const roleOptions = [
    { value: ROLE_VALUES.OPERADOR, label: t('users-page.standard-user-profile') },
    { value: ROLE_VALUES.SUPERVISOR, label: 'Supervisor' },
    { value: ROLE_VALUES.ADMIN, label: t('users-page.admin-profile') },
    ...(canAssignSuperAdmin ? [{ value: ROLE_VALUES.SUPER_ADMIN, label: 'Superadmin' }] : [])
  ];

  const onSubmit = async (data: FormValues) => {
    try {
      if (isEdit && editing) {
        const payload: UpdateUserPayload = {
          name: data.name.trim(),
          email: data.email.trim(),
          username: data.username.trim(),
          roles: [data.role],
          enabled: data.enabled
        };
        await UsersService.updateUser(editing.id, payload);
        AlertService.toastSuccess(t('users-page.edit-user-success'));
      } else {
        const payload: CreateUserPayload = {
          name: data.name.trim(),
          email: data.email.trim(),
          username: data.username.trim(),
          password: data.password,
          roles: [data.role],
          enabled: data.enabled,
          forcePasswordChange: data.forcePasswordChange
        };
        await UsersService.createUser(payload);
        AlertService.toastSuccess(t('users-page.add-user-success'));
      }
      onSaved();
      onClose();
    } catch (error: any) {
      AlertService.toastError(error?.error || error?.status || t('users-page.user-error'));
    }
  };

  return (
    <AnimatedModal isOpen={isOpen} onClose={onClose}>
      <div
        className="card shadow-lg border-0 rounded-4"
        style={{ width: 'min(560px, 92vw)', maxHeight: '92vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-header border-0 pt-6 d-flex align-items-center justify-content-between">
          <div className="d-flex flex-column">
            <h3 className="fw-bolder m-0">
              {isEdit
                ? t('users-page.modal-add-user.edit-user-title')
                : t('users-page.modal-add-user.add-user-title')}
            </h3>
            <span className="text-muted fs-7">{t('users-page.modal-add-user.data-user-text')}</span>
          </div>
          <button
            type="button"
            className="btn btn-icon btn-sm btn-active-light-primary"
            onClick={onClose}
          >
            <Icon icon="ki-cross" size="fs-1" />
          </button>
        </div>

        <div className="card-body pt-4">
          <form onSubmit={handleSubmit(onSubmit)} className="animate-slide">
            <div className="fv-row mb-5">
              <Input
                label={t('users-page.modal-add-user.full-name-label')}
                requiredIndicator="required"
                placeholder={t('users-page.modal-add-user.full-name-placeholder')}
                errors={errors.name}
                {...register('name', {
                  required: 'El nombre completo es obligatorio.',
                  minLength: { value: 2, message: '2' }
                })}
              />
            </div>

            <div className="fv-row mb-5">
              <Input
                label={t('users-page.modal-add-user.user-name-label')}
                requiredIndicator="required"
                placeholder={t('users-page.modal-add-user.user-name-placeholder')}
                errors={errors.username}
                {...register('username', {
                  required: 'El nombre de usuario es obligatorio.',
                  minLength: { value: 2, message: '2' }
                })}
              />
            </div>

            <div className="fv-row mb-5">
              <Input
                label={t('users-page.modal-add-user.email-label')}
                requiredIndicator="required"
                type="email"
                placeholder={t('users-page.modal-add-user.email-placeholder')}
                errors={errors.email}
                {...register('email', {
                  required: 'El correo electrónico es obligatorio.',
                  pattern: {
                    value: emailRegex,
                    message: 'Ingresa un correo electrónico válido (ej. usuario@dominio.com).'
                  }
                })}
              />
            </div>

            {!isEdit && (
              <div className="fv-row mb-5">
                <Input
                  label={t('general.password', 'Contraseña')}
                  requiredIndicator="required"
                  type="password"
                  placeholder="••••••••"
                  errors={errors.password}
                  {...register('password', {
                    required: 'La contraseña es obligatoria.',
                    minLength: { value: 8, message: '8' }
                  })}
                />
              </div>
            )}

            <div className="fv-row mb-5">
              <label className="form-label required">
                {t('users-page.modal-add-user.profile-label')}
              </label>
              <select
                className="form-select form-select-solid"
                {...register('role', { required: true })}
              >
                {roleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="d-flex flex-column gap-3 mb-6">
              <label className="form-check form-switch form-check-custom form-check-solid">
                <input className="form-check-input" type="checkbox" {...register('enabled')} />
                <span className="form-check-label fw-semibold text-gray-700">
                  {t('users-page.rolePermissions.enabled')}
                </span>
              </label>

              {!isEdit && (
                <label className="form-check form-switch form-check-custom form-check-solid">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    {...register('forcePasswordChange')}
                  />
                  <span className="form-check-label fw-semibold text-gray-700">
                    {t('users-page.modal-add-user.recover-password-title')}
                  </span>
                </label>
              )}
            </div>

            <div className="d-flex justify-content-end gap-3">
              <button
                type="button"
                className="btn btn-light btn-active-light-primary"
                onClick={onClose}
              >
                {t('general.cancel', 'Cancelar')}
              </button>
              <Button
                type="submit"
                className="btn btn-primary"
                disabled={!isValid}
                isLoading={isSubmitting}
              >
                {isEdit ? t('general.save', 'Guardar') : t('users-page.add-users-btn')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </AnimatedModal>
  );
}

export default UserFormModal;
